<script setup>
import {
    BaseButton,
    BaseEmptyState,
    BaseInputText,
    BaseIcon,
    BaseLoading
} from '@edsights/ui-core';
import PastCampaignCard from '@/views/RequestCampaignForm/components/PastCampaignCard.vue';
import { debounce } from 'lodash';
import Alerts from '@/lib/alerts';
import v2CollectionManager from '@/services/v2CollectionManager';
import { useRootStore } from '@/stores';
import { onBeforeMount, ref } from 'vue';

defineProps({
    bindings: {
        type: Object,
        required: true
    }
});
const pastCampaignName = ref('');
const rootStore = useRootStore();
const loading = ref(false);
const loadingNextPage = ref(false);
const pastCampaignList = ref([]);

const pastCampaignCollection = v2CollectionManager.create({
    listType: 'adminPastCampaigns',
    paginationOptions: {
        size: 15
    },
    filters: {
        search: null,
        schoolId: rootStore.userSchoolId
    },
    errorHandlerOptions: {
        enableAlert: true,
        rethrow: true
    }
});

const getPastCampaigns = async () => {
    try {
        loading.value = true;
        await pastCampaignCollection.refresh();
        pastCampaignList.value = pastCampaignCollection.list ?? [];
    } catch (error) {
        console.error('Error fetching past custom campaigns:', error);

        Alerts.alert({
            type: 'error',
            message: 'Failed to load past custom campaigns. Please try again later.'
        });
    } finally {
        loading.value = false;
    }
};

const debouncedFilterByName = debounce(async (searchText) => {
    pastCampaignCollection.filters.search = null;

    if (searchText) {
        pastCampaignName.value = searchText;
        pastCampaignCollection.filters.search = searchText;
    }

    await getPastCampaigns();
}, 300);

const loadNextPage = async () => {
    if (pastCampaignCollection.pagination.next !== null) {
        loadingNextPage.value = true;
        await pastCampaignCollection.addNextPage();
        pastCampaignList.value = pastCampaignCollection.list ?? [];
        loadingNextPage.value = false;
    }
};

onBeforeMount(async () => {
    await getPastCampaigns();
});
</script>

<template>
    <div class="past-campaign-container">
        <div class="past-campaign">
            <BaseInputText
                class="past-campaign__input"
                label="Search past custom campaigns"
                hideLabel
                placeholder="Search for past custom campaigns..."
                :modelValue="pastCampaignName"
                @update:modelValue="debouncedFilterByName"
            />
            <BaseIcon class="past-campaign__icon" icon="search" />
        </div>

        <div class="past-campaign-results">
            <div v-if="loading" class="content-container full-grid-column">
                <BaseLoading />
            </div>
            <div
                v-else-if="!pastCampaignList || pastCampaignList.length === 0"
                class="content-container full-grid-column"
            >
                <BaseEmptyState
                    icon="book"
                    message="No past custom campaigns found"
                    sub-message="Try searching with different keywords or check back later."
                />
            </div>
            <template v-else>
                <PastCampaignCard
                    v-for="campaign in pastCampaignList"
                    :bindings="bindings"
                    :key="campaign.id"
                    :campaign="campaign"
                    class="past-campaign-card-result"
                    @click="$emit('pastCampaignSelected', campaign)"
                />
            </template>
        </div>

        <div
            v-if="pastCampaignCollection.pagination.next !== null && !loading"
            class="search-footer"
        >
            <BaseButton
                class="load-more-button"
                label="Load More"
                :loading="loadingNextPage"
                @click="loadNextPage"
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables.scss';
.past-campaign-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: stretch;
}
.past-campaign {
    display: flex;
    align-items: center;
    margin: 1rem 0;
    width: 100%;
    position: relative;

    &__input {
        position: relative;
        width: 100%;
    }

    :deep(.ui-core-base-input-text) {
        width: 100%;
    }

    &__icon {
        position: absolute;
        cursor: pointer;
        top: 50%;
        transform: translateY(-50%);
        right: 0.7rem;
    }
}
.past-campaign-results {
    margin: 1rem 0;
    display: grid;
    grid-template-columns: 32% 32% 32%;
    gap: 2rem;
    width: 100%;

    .past-campaign-card-result {
        display: flex;
        cursor: pointer;
        transition: transform 0.2s;

        &:hover {
            transform: scale(1.02);
        }

        &__content {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin: 1rem;
        }

        :deep(.pi) {
            font-size: 2rem;
            color: $edsights-blue;
        }
    }

    .full-grid-column {
        grid-column: 1 / -1;
        width: 100%;
    }

    .content-container {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        width: 100%;
        min-height: 250px;
    }
}
.search-footer {
    display: flex;
    justify-content: center;

    .load-more-button {
        padding: 1rem 0;

        :deep(.p-button) {
            width: 200px;
        }
    }
}
@media (max-width: 900px) {
    .past-campaign-results {
        grid-template-columns: 100%;
    }
}
</style>
